import { useState } from "react";

import { AnalyticsEvent } from "@vega-api/custom/analytics/analyticsEvents";
import { useAnalytics } from "@vega-api/custom/hooks/useAnalytics";
import { useGetCoInvestLpPermissions } from "@vega-api/generated/coinvest-lp-permissions/coinvest-lp-permissions";
import { CoinvestDocumentType } from "@vega-api/generated/types/coinvestDocumentType";
import { type DealOpportunityLpViewResponseV3 } from "@vega-api/generated/types/dealOpportunityLpViewResponseV3";
import { DealStatusEnum } from "@vega-api/generated/types/dealStatusEnum";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import {
  trackCoInvestNdaDismissed,
  trackCoInvestNdaOpened,
  trackCoInvestTeaserViewed,
} from "@vega-core/utils/posthog/coInvestAnalytics";
import { useOrganization } from "@vega-modules/hooks/useOrganization";

import { useCoInvestDocument } from "./useCoInvestDocument";
import { useNdaSignature } from "./useNdaSignature";

type UseNdaPdfViewerProps = {
  data?: DealOpportunityLpViewResponseV3;
  dealOpportunityId: string;
  hasRejectedOffer?: boolean;
};

export const useNdaPdfViewer = ({
  data,
  dealOpportunityId,
  hasRejectedOffer = false,
}: UseNdaPdfViewerProps) => {
  const { trackEvent } = useAnalytics();
  const { selectedOrgId: limitedPartnerId } = useOrganization();

  const pdfDocument = data?.ndaStatuses?.documents.find(
    (d) => d.typeOfDocument === CoinvestDocumentType.PDF,
  );
  const ndaDocumentId = pdfDocument?.documentId;
  const NIL_UUID = "00000000-0000-0000-0000-000000000000";
  const hasNdaDocument = !!ndaDocumentId && ndaDocumentId !== NIL_UUID;

  const { handleSignNda, isSigningNda } = useNdaSignature({
    dealOpportunityId,
    dealId: data?.dealOpportunity.dealId,
    documentId: pdfDocument?.documentId,
  });
  const { data: lpPermissions } = useGetCoInvestLpPermissions({
    dealOpportunityId,
  });

  const canSignNda = lpPermissions?.data?.canSignNda ?? false;
  const ndaAgreed = data?.ndaStatuses?.isSigned ?? false;
  const isDealClosed = data?.deal.status === DealStatusEnum.CLOSED;

  const pdfViewer = useBoolean(!ndaAgreed && hasNdaDocument);
  const [currentDocumentId, setCurrentDocumentId] = useState<string | null>(
    hasNdaDocument ? ndaDocumentId : null,
  );
  const [isViewingTeaser, setIsViewingTeaser] = useState(false);
  const [currentDocumentFilename, setCurrentDocumentFilename] = useState("");

  const { presignedUrl, isLoading: isLoadingPresignedUrl } =
    useCoInvestDocument(
      currentDocumentId,
      isViewingTeaser,
      !!currentDocumentId,
    );

  const handleViewDocument = (
    documentId: string,
    isTeaser = false,
    filename = "",
  ) => {
    setCurrentDocumentId(documentId);
    setIsViewingTeaser(isTeaser);
    setCurrentDocumentFilename(filename);
    pdfViewer.setTrue();

    if (!isTeaser && data?.ndaStatuses) {
      trackEvent(AnalyticsEvent.COINVEST_NDA_OPENED, undefined, {
        documentId,
        dealId: data.dealOpportunity.dealId,
        dealOpportunityId: data.dealOpportunity.id,
        limitedPartnerId,
      });
      trackCoInvestNdaOpened({
        dealOpportunityId: data.dealOpportunity.id,
        dealId: data.dealOpportunity.dealId,
      });
    } else if (isTeaser && data) {
      trackCoInvestTeaserViewed({
        dealOpportunityId: data.dealOpportunity.id,
        dealId: data.dealOpportunity.dealId,
      });
    }
  };

  const handleNDAAgree = async () => {
    await handleSignNda();
    pdfViewer.setFalse();
  };

  const handleNDADecline = () => {
    trackEvent(AnalyticsEvent.COINVEST_NDA_DISMISSED, undefined, {
      documentId: pdfDocument?.documentId,
      dealId: data?.dealOpportunity.dealId ?? "",
      dealOpportunityId: data?.dealOpportunity.id ?? "",
      limitedPartnerId,
    });
    trackCoInvestNdaDismissed({
      dealOpportunityId: data?.dealOpportunity.id ?? "",
      dealId: data?.dealOpportunity.dealId,
    });
    pdfViewer.setFalse();
  };

  const getHeaderActions = () => {
    if (
      isViewingTeaser ||
      ndaAgreed ||
      hasRejectedOffer ||
      !canSignNda ||
      isDealClosed
    ) {
      return undefined; // No header actions for teaser, if NDA already agreed, deal rejected, no permission to sign, or deal closed
    }

    return (
      <div className="flex gap-2">
        <ThemedButton
          dataTestId="nda-decline-button"
          onClick={handleNDADecline}
          size="small"
          variant="secondary"
        >
          {m.btn_notInterested()}
        </ThemedButton>
        <ThemedButton
          dataTestId="nda-agree-button"
          disabled={isSigningNda}
          onClick={handleNDAAgree}
          size="small"
          variant="primary"
        >
          {isSigningNda ? m.btn_loading() : m.btn_agree()}
        </ThemedButton>
      </div>
    );
  };

  return {
    pdfViewer,
    currentDocumentId,
    isViewingTeaser,
    presignedUrl,
    isLoadingPresignedUrl,
    currentDocumentFilename,
    handleViewDocument,
    getHeaderActions,
  };
};
